// =====================================================================================
// INVRT Radar - web console - HIGH-INTENT Settings (Story N3)
//
// READ-ONLY in v1, and honest about what this console can know: the proxy route family it
// reads (the app's own /v1/radar/* paths - never an upstream origin), the provider named
// without any origin or credential, and the caps that are real client-side facts (the
// bounded page size; the campaign scope the subscription binds). There are no inputs and
// no mutations here, and the screen performs no seam reads of its own: the entitlement
// gate above it already proved this organisation's access.
// =====================================================================================

import type { ReactNode } from "react";
import { LEADS_PAGE_LIMIT } from "./useRadar";

const PROXY_ROUTES = [
  "/v1/radar/campaigns",
  "/v1/radar/search",
  "/v1/radar/campaigns/{id}/runs",
  "/v1/radar/campaigns/{id}/stats",
];

export function SettingsPanel() {
  return (
    <section aria-label="HIGH-INTENT settings" className="flex flex-col gap-4">
      <div>
        <h2 id="hi-settings-heading" className="text-lg font-semibold text-ink">
          Settings
        </h2>
        <p className="mt-1 text-sm text-ink-muted">
          How HIGH-INTENT reaches this console. Read-only in v1: nothing on this screen can
          be changed here.
        </p>
      </div>

      <CardFlushed title="Data route">
        <p className="text-sm text-ink-muted">
          All reads go through RADAR's own API under these proxy paths. The browser never
          calls the analysis service directly and never holds any provider credential.
        </p>
        <ul className="mt-2 flex flex-col gap-1">
          {PROXY_ROUTES.map((route) => (
            <li key={route} className="font-mono text-xs text-ink">
              {route}
            </li>
          ))}
        </ul>
      </CardFlushed>

      <CardFlushed title="Provider">
        <p className="text-sm text-ink-muted">
          HIGH-INTENT analysis service, reached only through RADAR's server proxy. The
          provider's address and credentials stay on the server; nothing about them exists
          in this browser.
        </p>
      </CardFlushed>

      <CardFlushed title="Caps">
        <ul className="flex flex-col gap-1 text-sm text-ink-muted">
          <li>Search results are capped at {LEADS_PAGE_LIMIT} leads per page.</li>
          <li>
            Search is capped to the campaigns bound to this organisation's subscription;
            the server refuses anything outside that scope.
          </li>
          <li>Settings are read-only in v1. Changes come from a future release.</li>
        </ul>
      </CardFlushed>
    </section>
  );
}

function CardFlushed({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-card border border-line bg-surface p-5">
      <h3 className="text-base font-semibold text-ink">{title}</h3>
      <div className="mt-2">{children}</div>
    </div>
  );
}
